import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export const ToastContext = createContext()

export const useToast = () => useContext(ToastContext)

/**
 * Hard ceiling on queued toasts. Repeats are collapsed before this applies, so
 * reaching it means 20 *distinct* messages are outstanding — a cascade, not
 * normal use. Past the cap new messages are dropped rather than evicting
 * queued ones, so the user keeps the earliest (usually causal) errors instead
 * of the tail of a storm.
 */
export const MAX_TOASTS = 20

let addToastRef = null

export const addToastOutsideComponent = (message, type) => {
  if (addToastRef) {
    addToastRef(message, type)
  } else {
    console.warn('ToastProvider is not mounted, unable to show toast.')
  }
}

export function useToastProviderLogic({ children }) {
  // One queue, where index 0 is what's on screen. Deriving the visible toast
  // from the queue rather than mirroring it into separate state is what makes
  // de-duplication possible: a repeat has to be matched against the displayed
  // toast as well as the pending ones, and a single array can be checked
  // atomically inside one updater.
  const [toasts, setToasts] = useState([])
  const nextIdRef = useRef(0)

  const addToast = useCallback((message, type) => {
    // Generated outside the updater so the updater stays pure — StrictMode
    // invokes it twice, and a ref bump inside would run twice per call.
    nextIdRef.current += 1
    const id = nextIdRef.current

    setToasts((prev) => {
      // Collapse repeats into an occurrence count. A failing backend emits the
      // same error once per poll per query, and useApiQuery cannot suppress
      // them (axios builds a new error object per request, so its
      // previous-error guard never matches). Without this the queue becomes a
      // conveyor belt draining at one toast per 5s that the user cannot
      // outrun, still going long after the backend recovers.
      const existing = prev.findIndex(
        (toast) => toast.message === message && toast.type === type,
      )
      if (existing !== -1) {
        const next = [...prev]
        next[existing] = { ...next[existing], count: next[existing].count + 1 }
        return next
      }

      if (prev.length >= MAX_TOASTS) return prev

      return [...prev, { id, message, type, count: 1 }]
    })
  }, [])

  // Dismisses the toast on screen and promotes the next one.
  const removeToast = useCallback(() => {
    setToasts((prev) => prev.slice(1))
  }, [])

  // The escape hatch this queue exists to provide: a pile of errors the user
  // has read and wants gone, without clicking through each one at 5s a turn.
  const dismissAll = useCallback(() => {
    setToasts([])
  }, [])

  useEffect(() => {
    addToastRef = addToast
    return () => {
      addToastRef = null
    }
  }, [addToast])

  return {
    children,
    currentToast: toasts[0] ?? null,
    queuedCount: Math.max(0, toasts.length - 1),
    addToast,
    removeToast,
    dismissAll,
  }
}
